import {initContent} from '@/services/content/content';
import eventMessagesService from '@/services/events/eventMessagesService';
import appRouterService from '@/services/route/appRouterService';
import type {AppPadding} from '@/state/slices/appSlice';
import {setInitialized, setLanguageCode, setMarketCode, setPadding} from '@/state/slices/appSlice';
import {setBadgesData} from '@/state/slices/loyaltySlice';
import {dispatch} from '@/state/store';
import type {BadgesData} from '@/types/loyalty';

type AppInitPayload = {
    marketCode?: string;
    languageCode?: string;
    badgesConfiguration?: BadgesData;
    paddingTop?: number;
    paddingBottom?: number;
    paddingBottomMenu?: number;
};

/**
 * TODO: Remove once AEM content is migrated from "v2" market folders to plain market codes.
 * The host app sends market codes like "gbv2" but AEM content now lives under "gb".
 * This strips the trailing "v2" suffix so the correct AEM persisted query path is resolved.
 */
const normalizeMarketCode = (code: string): string => {
    return code.replace(/v2$/i, '');
};

/**
 * Handles UWM_INIT_MARKET_LOCALE.
 * Expected payload: { marketCode, languageCode, badgesConfiguration }
 */
export const proceedAppInit = async (payload: AppInitPayload | null | undefined) => {
    try {
        const {marketCode, languageCode, badgesConfiguration, paddingTop, paddingBottom, paddingBottomMenu} =
            payload ?? {};

        if (!marketCode || !languageCode) {
            eventMessagesService.sendModuleInitializationError(
                `Initialized data is not correct. MarketCode: ${marketCode} LanguageCode: ${languageCode}`
            );
            return;
        }

        if (!badgesConfiguration) {
            eventMessagesService.sendModuleInitializationError(
                'Initialized data is not correct: badgesConfiguration is missing'
            );
            return;
        }

        await initContent(normalizeMarketCode(marketCode), languageCode);

        dispatch(setMarketCode(marketCode));
        dispatch(setLanguageCode(languageCode));
        dispatch(setBadgesData(badgesConfiguration));

        const padding: AppPadding = {
            ...(paddingTop != null && {top: paddingTop}),
            ...(paddingBottom != null && {bottom: paddingBottom}),
            ...(paddingBottomMenu != null && {bottomMenu: paddingBottomMenu}),
        };
        dispatch(setPadding(padding));

        dispatch(setInitialized(true));
        appRouterService.replaceToBadgesPage();
        eventMessagesService.sendDataSuccessReceivedMessage();
    } catch (e: unknown) {
        const message = e instanceof Error ? e.message : String(e);
        eventMessagesService.sendModuleInitializationError(message);
    }
};

/**
 * Handles UWM_REFRESH_BADGES_DATA — used after the user returns from an
 * external link. Only the badges data is updated; market/language/content and
 * the current route are intentionally left untouched so the user stays on the
 * page they were on (e.g. /in-progress) until UI guards decide otherwise.
 */
export const proceedBadgesDataRefresh = (payload: {badgesConfiguration?: BadgesData} | null | undefined) => {
    const {badgesConfiguration} = payload ?? {};
    if (!badgesConfiguration) return;

    dispatch(setBadgesData(badgesConfiguration));
    eventMessagesService.sendDataSuccessReceivedMessage();
};
